/**
 * Data transformers for converting database structures to component prop interfaces.
 * These pure functions map database JSONB structures to component props.
 *
 * Related Spec: uk-us-expansion
 * Task: Task 12a - Regional field name extraction
 *
 * @module productDataTransformers
 */

import {
  extractFuelConsumption,
  normalizeFuelConsumptionToLPer100km,
  getAccelerationField,
} from '../hooks/calculatorHelpers';

/**
 * Transforms vehicle data from database structures into ProductData component props.
 *
 * This function converts data from vehicle_models, vehicle_model_images, and
 * user_model_fit_scores tables into the format expected by the ProductData component.
 *
 * @param {object} modelData - Vehicle model data from vehicle_models table
 * @param {string} modelData.make - Vehicle make (e.g., "Mercedes-Benz")
 * @param {string} modelData.model - Vehicle model (e.g., "E-Class Estate")
 * @param {string} modelData.year_range - Year range (e.g., "2013-2016")
 * @param {string} modelData.short_description - Brief vehicle description
 * @param {Array<object>} modelImages - Array of image objects from vehicle_model_images table
 * @param {string} modelImages[].url - Image URL
 * @param {string} modelImages[].angle_view - Camera angle (e.g., "front-left", "side")
 * @param {boolean} modelImages[].is_default_display - Whether this is the default display image
 * @param {object} fitScore - Fit score data from user_model_fit_scores table
 * @param {number} fitScore.score_value - Fit score value (0-100 integer)
 * @param {string} fitScore.placeholder_image_url - Brand logo or fallback image URL
 * @param {string} fitScore.image_fidelity - Image fidelity status
 *
 * @returns {object} Transformed data matching ProductData component props
 * @returns {string} return.name - Formatted vehicle name (make + model)
 * @returns {string} return.model - Formatted model year string
 * @returns {string} return.description - Vehicle description
 * @returns {number} return.styleMatch - Style match percentage (0-100)
 * @returns {Array<object>} return.images - Transformed image array
 * @returns {string} return.placeholderImageUrl - Brand logo or fallback image URL
 * @returns {string} return.imageFidelity - Image fidelity status
 *
 * @example
 * const transformed = transformVehicleDataForProductData(
 *   {
 *     make: "Mercedes-Benz",
 *     model: "E-Class Estate",
 *     year_range: "2013-2016",
 *     short_description: "Luxurious executive wagon..."
 *   },
 *   [
 *     { url: "https://...", angle_view: "front-left", is_default_display: true },
 *     { url: "https://...", angle_view: "side", is_default_display: false }
 *   ],
 *   { score_value: 92, placeholder_image_url: "https://...", image_fidelity: "confirmed" }
 * );
 * // Returns:
 * // {
 * //   name: "Mercedes-Benz E-Class Estate",
 * //   model: "2013-2016 Model",
 * //   description: "Luxurious executive wagon...",
 * //   styleMatch: 92,
 * //   images: [...],
 * //   placeholderImageUrl: "https://...",
 * //   imageFidelity: "confirmed"
 * // }
 */
export function transformVehicleDataForProductData(modelData, modelImages, fitScore) {
  // Handle null/undefined inputs with safe defaults
  if (!modelData) {
    return {
      name: '',
      model: '',
      description: '',
      styleMatch: 0,
      images: [],
      placeholderImageUrl: null,
      imageFidelity: null,
    };
  }

  // Extract and validate model data
  const make = modelData.make || '';
  const model = modelData.model || '';
  const yearRange = modelData.year_range || '';
  const shortDescription = modelData.short_description || '';

  // Extract fit score with default
  const scoreValue = fitScore?.score_value ?? 0;

  // Extract placeholder image URL and image fidelity from fitScore
  const placeholderImageUrl = fitScore?.placeholder_image_url || null;
  const imageFidelity = fitScore?.image_fidelity || null;

  // Transform images array (or return empty array if none provided)
  const transformedImages = Array.isArray(modelImages)
    ? modelImages.map((img) => ({
        url: img.url || '',
        angle_view: img.angle_view || '',
        is_default_display: img.is_default_display || false,
      }))
    : [];

  return {
    name: `${make} ${model}`.trim(),
    model: yearRange ? `${yearRange} Model` : '',
    description: shortDescription,
    styleMatch: scoreValue,
    images: transformedImages,
    placeholderImageUrl,
    imageFidelity,
  };
}

/**
 * Transforms personalized content data from database structures into ProductFit component props.
 *
 * This function extracts good_fit and bad_fit explanations from the user_model_page_content
 * table's personalized_content JSONB structure and formats them into the cardsData
 * array expected by the ProductFit component.
 *
 * @param {object} personalizedContent - Personalized content from user_model_page_content table
 * @param {object} personalizedContent.personalized_content - JSONB personalized content object
 * @param {Array<string>} personalizedContent.personalized_content.good_fit - Array of positive fit reasons
 * @param {Array<string>} personalizedContent.personalized_content.bad_fit - Array of negative fit reasons
 *
 * @returns {object} Transformed data matching ProductFit component props
 * @returns {Array<object>} return.cardsData - Array of card objects for ProductFit
 * @returns {string} return.cardsData[].type - Card type ('good' or 'poor')
 * @returns {string} return.cardsData[].title - Card title ('great' or 'poor')
 * @returns {Array<string>} return.cardsData[].items - Array of explanation strings
 *
 * @example
 * const transformed = transformFitScoreForProductFit({
 *   personalized_content: {
 *     good_fit: ["Spacious cargo area", "Excellent fuel efficiency"],
 *     bad_fit: ["Higher than budget", "Limited color options"]
 *   }
 * });
 * // Returns:
 * // {
 * //   cardsData: [
 * //     { type: 'good', title: 'great', items: ["Spacious cargo area", "Excellent fuel efficiency"] },
 * //     { type: 'poor', title: 'poor', items: ["Higher than budget", "Limited color options"] }
 * //   ]
 * // }
 *
 * @example
 * // Handle missing or incomplete data
 * const emptyResult = transformFitScoreForProductFit(null);
 * // Returns: { cardsData: [] }
 */
export function transformFitScoreForProductFit(personalizedContent) {
  // Handle null/undefined input
  if (!personalizedContent?.personalized_content) {
    return {
      cardsData: [],
    };
  }

  const content = personalizedContent.personalized_content;

  // Extract good_fit and bad_fit arrays with safe defaults
  // Limit to maximum 4 items per card for better UI presentation
  const MAX_ITEMS = 4;
  const goodFitItems = Array.isArray(content.good_fit) ? content.good_fit.slice(0, MAX_ITEMS) : [];
  const badFitItems = Array.isArray(content.bad_fit) ? content.bad_fit.slice(0, MAX_ITEMS) : [];

  // Build cardsData array according to component prop interface
  const cardsData = [
    {
      type: 'good',
      title: 'great',
      items: goodFitItems,
    },
    {
      type: 'poor',
      title: 'poor',
      items: badFitItems,
    },
  ];

  return {
    cardsData,
  };
}

/**
 * Transforms personalized content data from database structures into ProductInfo component props.
 *
 * This function extracts six accordion objects from the user_model_page_content table's
 * personalized_content JSONB structure and converts them into the accordion items format
 * expected by the ProductInfo component.
 *
 * The six accordions represent different aspects of vehicle evaluation:
 * - Driving Experience
 * - Real-World Practicality
 * - Interior Comfort & Tech
 * - Safety in Context
 * - Long-Term Ownership
 * - Powertrain & Environment
 *
 * @param {object} personalizedContent - Personalized content from user_model_page_content table
 * @param {object} personalizedContent.personalized_content - JSONB personalized content object
 * @param {object} personalizedContent.personalized_content.accordion_driving_experience - Driving experience accordion
 * @param {object} personalizedContent.personalized_content.accordion_real_world_practicality - Practicality accordion
 * @param {object} personalizedContent.personalized_content.accordion_interior_comfort_tech - Interior/comfort accordion
 * @param {object} personalizedContent.personalized_content.accordion_safety_in_context - Safety accordion
 * @param {object} personalizedContent.personalized_content.accordion_long_term_ownership - Ownership accordion
 * @param {object} personalizedContent.personalized_content.accordion_powertrain_environment - Powertrain/environment accordion
 *
 * Each accordion object structure:
 * @param {string} accordion.title - Accordion section title
 * @param {number} accordion.rating - Rating value (0-5 with 0.5 increments)
 * @param {object} accordion.content - Content object containing match details
 * @param {string} accordion.content.match_statement - Summary statement of user fit
 * @param {Array<object>} accordion.content.key_metrics - Array of key metric objects
 * @param {string} accordion.content.key_metrics[].label - Metric label
 * @param {string} accordion.content.key_metrics[].value - Metric value
 * @param {string} accordion.content.key_metrics[].context - Contextual explanation
 * @param {string} accordion.content.key_metrics[].valueType - Visual indicator ('normal', 'warning', 'bad')
 * @param {string} accordion.content.user_fit_analysis - Detailed fit analysis text
 *
 * @returns {object} Transformed data matching ProductInfo component props
 * @returns {string} return.title - Section title ("Vehicle Details")
 * @returns {Array<object>} return.items - Array of accordion item objects
 * @returns {string} return.items[].title - Accordion title
 * @returns {number} return.items[].rating - Accordion rating (0-5)
 * @returns {object} return.items[].content - Accordion content object
 * @returns {string} return.items[].content.match_statement - Match summary
 * @returns {Array<object>} return.items[].content.key_metrics - Key metrics array
 * @returns {string} return.items[].content.user_fit_analysis - Fit analysis text
 *
 * @example
 * const transformed = transformPersonalizedContentForProductInfo({
 *   personalized_content: {
 *     accordion_driving_experience: {
 *       title: "Driving Experience",
 *       rating: 4.5,
 *       content: {
 *         match_statement: "Great driving dynamics for your needs",
 *         key_metrics: [
 *           {
 *             label: "0-60 mph",
 *             value: "6.2s",
 *             context: "Quick acceleration for highway merging",
 *             valueType: "normal"
 *           }
 *         ],
 *         user_fit_analysis: "This vehicle offers excellent handling..."
 *       }
 *     },
 *     accordion_real_world_practicality: { ... },
 *     // ... other accordions
 *   }
 * });
 * // Returns:
 * // {
 * //   title: "Vehicle Details",
 * //   items: [
 * //     {
 * //       title: "Driving Experience",
 * //       rating: 4.5,
 * //       content: {
 * //         match_statement: "Great driving dynamics for your needs",
 * //         key_metrics: [...],
 * //         user_fit_analysis: "This vehicle offers excellent handling..."
 * //       }
 * //     },
 * //     // ... other items
 * //   ]
 * // }
 *
 * @example
 * // Handle missing or incomplete data
 * const emptyResult = transformPersonalizedContentForProductInfo(null);
 * // Returns: { title: "Vehicle Details", items: [] }
 */
export function transformPersonalizedContentForProductInfo(personalizedContent) {
  // Handle null/undefined input
  if (!personalizedContent || !personalizedContent.personalized_content) {
    return {
      title: 'Vehicle Details',
      items: [],
    };
  }

  const content = personalizedContent.personalized_content;

  // Define the six accordion field names in order
  const accordionFields = [
    'accordion_driving_experience',
    'accordion_real_world_practicality',
    'accordion_interior_comfort_tech',
    'accordion_safety_in_context',
    'accordion_long_term_ownership',
    'accordion_powertrain_environment',
  ];

  // Extract and validate each accordion object
  const items = accordionFields
    .map((fieldName) => content[fieldName])
    .filter((accordion) => {
      // Filter out null, undefined, or invalid accordion items
      if (!accordion) {
        return false;
      }
      // Ensure accordion has required structure
      if (typeof accordion !== 'object') {
        return false;
      }
      // Validate required fields exist
      if (!accordion.title || accordion.rating === undefined || !accordion.content) {
        return false;
      }
      return true;
    })
    .map((accordion) => ({
      title: accordion.title,
      rating: accordion.rating,
      content: {
        match_statement: accordion.content.match_statement || '',
        key_metrics: Array.isArray(accordion.content.key_metrics)
          ? accordion.content.key_metrics.map((metric) => ({
              label: metric.label || '',
              value: metric.value || '',
              context: metric.context || '',
              valueType: metric.valueType || 'normal',
            }))
          : [],
        user_fit_analysis: accordion.content.user_fit_analysis || '',
      },
    }));

  return {
    title: 'Vehicle Details',
    items,
  };
}

/**
 * Transforms vehicle data from database structures into ProductSide cost calculator props.
 *
 * This function extracts cost-related data from vehicle_models, user_model_page_content,
 * and user_model_fit_scores tables and converts it into the format expected by the
 * ProductSide component's cost calculator.
 *
 * REGIONAL FIELD EXTRACTION (Task 12a):
 * The LLM returns different field names based on region:
 * - EU: fuel_consumption_l_100km_combined, acceleration_0_100_kmh_sec
 * - UK: fuel_consumption_mpg_imperial_combined, acceleration_0_60_mph_sec
 * - US: fuel_consumption_mpg_us_combined, acceleration_0_60_mph_sec
 *
 * This function extracts the correct field based on region and normalizes to L/100km
 * for calculator compatibility.
 *
 * @param {object} modelData - Vehicle model data from vehicle_models table
 * @param {object} modelData.static_primary_specs - Static primary specifications JSONB object
 * @param {Array<object>} modelData.static_primary_specs.engine_options - Array of engine option objects
 * @param {string} modelData.monthly_ownership_cost - Monthly ownership cost range string (e.g., "€100 - €160")
 * @param {object} personalizedContent - Personalized content from user_model_page_content table
 * @param {object} personalizedContent.personalized_content - JSONB personalized content object
 * @param {number} personalizedContent.personalized_content.estimated_annual_depreciation_percentage - Annual depreciation estimate
 * @param {object} fitScoreData - Fit score data from user_model_fit_scores table
 * @param {string} fitScoreData.estimated_price_range - Price range string (e.g., "€25,000 - €30,000")
 * @param {object} onboardingData - User's onboarding answers
 * @param {string} onboardingData.q1_driving_plan - Driving plan (e.g., "driving_balanced_mix")
 * @param {string} onboardingData.q2_annual_distance - Annual distance (e.g., "distance_km_5k_10k")
 * @param {'EU'|'UK'|'US'} region - User's region for extracting correct fuel consumption field
 *
 * @returns {object} Transformed data matching ProductSide cost calculator input props
 * @returns {string} return.estimated_price_range - Price range string (e.g., "€25,000 - €30,000")
 * @returns {number} return.estimated_annual_depreciation_percentage - Annual depreciation percentage (e.g., 15)
 * @returns {number} return.fuel_consumption_l_100km_combined - Combined fuel consumption in L/100km (normalized)
 * @returns {number} return.fuel_consumption_native - Native fuel consumption value (L/100km or MPG)
 * @returns {string} return.fuel_consumption_unit - Unit type ('l_100km' | 'mpg')
 * @returns {string} return.monthly_ownership_cost - Monthly ownership cost range string (e.g., "€100 - €160")
 * @returns {string} [return.onboarding_driving_plan] - User's driving plan preference (optional)
 * @returns {string} [return.onboarding_annual_distance_value] - User's annual distance range (optional)
 *
 * @example
 * // EU example - L/100km
 * const transformed = transformVehicleDataForProductSide(
 *   {
 *     static_primary_specs: {
 *       engine_options: [{ fuel_consumption_l_100km_combined: 7.5 }]
 *     },
 *     monthly_ownership_cost: "€100 - €160"
 *   },
 *   { personalized_content: { estimated_annual_depreciation_percentage: 15 } },
 *   { estimated_price_range: "€25,000 - €30,000" },
 *   null,
 *   'EU'
 * );
 * // Returns fuel_consumption_l_100km_combined: 7.5, fuel_consumption_native: 7.5
 *
 * @example
 * // UK example - Imperial MPG converted to L/100km
 * const transformed = transformVehicleDataForProductSide(
 *   {
 *     static_primary_specs: {
 *       engine_options: [{ fuel_consumption_mpg_imperial_combined: 45 }]
 *     },
 *     monthly_ownership_cost: "£80 - £140"
 *   },
 *   { personalized_content: { estimated_annual_depreciation_percentage: 12 } },
 *   { estimated_price_range: "£20,000 - £25,000" },
 *   null,
 *   'UK'
 * );
 * // Returns fuel_consumption_l_100km_combined: 6.28 (converted), fuel_consumption_native: 45
 */
export function transformVehicleDataForProductSide(
  modelData,
  personalizedContent,
  fitScoreData,
  onboardingData = null,
  region = 'EU'
) {
  // Handle null/undefined inputs with safe defaults
  const safeDefaults = {
    estimated_price_range: '',
    estimated_annual_depreciation_percentage: 0,
    fuel_consumption_l_100km_combined: 0,
    monthly_ownership_cost: '€100 - €200',
  };

  // Extract estimated_price_range from fitScoreData
  const estimatedPriceRange =
    fitScoreData?.estimated_price_range ?? safeDefaults.estimated_price_range;

  // Extract estimated_annual_depreciation_percentage from personalizedContent
  const estimatedAnnualDepreciationPercentage =
    personalizedContent?.personalized_content?.estimated_annual_depreciation_percentage ??
    safeDefaults.estimated_annual_depreciation_percentage;

  // Extract fuel consumption using regional field extraction (Task 12a)
  const engineOptions = modelData?.static_primary_specs?.engine_options;
  const fuelData = extractFuelConsumption(engineOptions, region);

  // Normalize to L/100km for calculator compatibility
  // If fallback was used (EU field on UK/US data), the value is already in L/100km
  let fuelConsumptionNormalized;
  if (fuelData.fallbackUsed) {
    // Fallback used EU field, value is already L/100km
    fuelConsumptionNormalized = fuelData.value;
  } else {
    // Convert native value to L/100km based on region
    fuelConsumptionNormalized = normalizeFuelConsumptionToLPer100km(fuelData.value, region);
  }

  // Extract acceleration using regional field (for display purposes)
  const accelerationField = getAccelerationField(region);
  const acceleration =
    Array.isArray(engineOptions) && engineOptions.length > 0
      ? engineOptions[0]?.[accelerationField] ?? null
      : null;

  // Extract monthly_ownership_cost from modelData - keep as string range
  const monthlyOwnershipCost =
    modelData?.monthly_ownership_cost ?? safeDefaults.monthly_ownership_cost;

  // Extract onboarding data if available
  const onboardingDrivingPlan = onboardingData?.q1_driving_plan;
  const onboardingAnnualDistance = onboardingData?.q2_annual_distance;

  return {
    estimated_price_range: estimatedPriceRange,
    estimated_annual_depreciation_percentage: estimatedAnnualDepreciationPercentage,
    // Normalized L/100km for calculator (backwards compatible)
    fuel_consumption_l_100km_combined: fuelConsumptionNormalized,
    // Native value and unit for display
    fuel_consumption_native: fuelData.value,
    fuel_consumption_unit: fuelData.unit,
    fuel_consumption_is_default: fuelData.isDefault,
    // Acceleration in regional units
    acceleration_native: acceleration,
    acceleration_unit: region === 'EU' ? '0-100 km/h' : '0-60 mph',
    monthly_ownership_cost: monthlyOwnershipCost,
    // Only include onboarding values if they exist (to avoid validation warnings)
    ...(onboardingDrivingPlan && { onboarding_driving_plan: onboardingDrivingPlan }),
    ...(onboardingAnnualDistance && { onboarding_annual_distance_value: onboardingAnnualDistance }),
  };
}
